import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, User, MapPin, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OpenPositionsSummary() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/positions/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSummary(response.data);
      } catch (error) {
        toast.error('Failed to fetch open positions summary');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><p className="text-slate-500">Loading summary...</p></div>;
  }

  const totalResumes = summary.reduce((s, p) => s + p.resumes, 0);
  const totalInterviews = summary.reduce((s, p) => s + p.interviews, 0);
  const totalClosures = summary.reduce((s, p) => s + p.closures, 0);

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/positions?format=${format}&status_filter=open`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `open_positions.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="open-positions-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Open Positions Summary</h1>
          <p className="text-slate-600">All open positions with recruiter allocations and deliverables</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="export-open-positions-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
              <Download size={16} className="mr-2" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="export-open-pos-excel" onClick={() => handleExport('excel')}>
              <FileSpreadsheet size={16} className="mr-2" />Export as Excel
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="export-open-pos-pdf" onClick={() => handleExport('pdf')}>
              <FileText size={16} className="mr-2" />Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 p-5 rounded">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1">Open Positions</p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900" data-testid="open-count">{summary.length}</p>
        </div>
        <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-200 p-5 rounded">
          <p className="text-xs font-bold uppercase tracking-wider text-pink-700 mb-1">Total Resumes</p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-pink-900" data-testid="total-resumes-count">{totalResumes}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 p-5 rounded">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Total Interviews</p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-amber-900" data-testid="total-interviews-count">{totalInterviews}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-5 rounded">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Total Closures</p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-emerald-900" data-testid="total-closures-count">{totalClosures}</p>
        </div>
      </div>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Position Title</TableHead>
              <TableHead className="font-bold text-blue-900">Client</TableHead>
              <TableHead className="font-bold text-blue-900">Location</TableHead>
              <TableHead className="font-bold text-blue-900">Assigned To</TableHead>
              <TableHead className="font-bold text-blue-900 text-center">Resumes</TableHead>
              <TableHead className="font-bold text-blue-900 text-center">Interviews</TableHead>
              <TableHead className="font-bold text-blue-900 text-center">Closures</TableHead>
              <TableHead className="font-bold text-blue-900">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                  <Briefcase size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No open positions found</p>
                </TableCell>
              </TableRow>
            ) : (
              summary.map((pos) => (
                <TableRow key={pos.id} data-testid={`open-pos-row-${pos.id}`} className="hover:bg-blue-50">
                  <TableCell className="font-semibold text-blue-900">{pos.title}</TableCell>
                  <TableCell>{pos.client_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <span className="flex items-center gap-1"><MapPin size={12} />{pos.location || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      <User size={12} className="text-blue-600" />
                      {pos.assigned_to_name || <span className="text-slate-400">Unassigned</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-semibold text-pink-700">{pos.resumes}</TableCell>
                  <TableCell className="text-center font-semibold text-amber-700">{pos.interviews}</TableCell>
                  <TableCell className="text-center font-semibold text-emerald-700">{pos.closures}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{pos.created_at ? new Date(pos.created_at).toLocaleDateString() : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
